import React, { useEffect, useRef, useState } from "react";
import { BadgeCheck, CircleX, RefreshCw } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { bankTransferAutoConfirmApiService } from "../../../infrastructure/api/company/bank-transfer-auto-confirm-api-service";

export const BankTransferBillingSuccess: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const started = useRef(false);
  const [error, setError] = useState("");
  // 주소에서 값을 지워도 라우터가 들고 있는 검색어는 그대로라 여기서 계속 읽을 수 있다
  const companyId = Number(params.get("companyId"));

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const checkoutKey = params.get("checkoutKey");
    const authKey = params.get("authKey");
    const customerKey = params.get("customerKey");
    // 카드 등록·변경으로 돌아온 경우. 구독 구매와 달리 checkoutKey 가 없다
    const billingMethodOnly = params.get("mode") === "billing-method";

    // 값을 손에 쥐었으면 주소에서 지운다.
    //
    // authKey 는 **카드로 자동결제를 걸 수 있는 인증값**이다. 주소창에 남으면
    // 방문기록·북마크에 남고, 화면을 찍어 보내거나 링크를 그대로 붙여넣는 순간 새어 나간다.
    // 특히 실패했을 때 사람들은 그 화면에 머무른다 — 성공하면 곧 다른 곳으로 넘어가지만
    // 실패하면 주소가 그대로 남는다.
    window.history.replaceState(null, "", window.location.pathname);

    if (!companyId || !authKey) {
      setError("결제 인증 정보가 올바르지 않습니다.");
      return;
    }

    const done = () =>
      window.setTimeout(
        () => navigate(`/company/${companyId}/payment/bank-transfer`, { replace: true }),
        900,
      );

    if (billingMethodOnly) {
      // customerKey 는 서버가 가진 값을 쓰므로 URL 값을 전달하지 않는다
      bankTransferAutoConfirmApiService
        .changeBillingMethod(companyId, authKey)
        .then(done)
        .catch((e) => setError(e instanceof Error ? e.message : "결제수단 등록에 실패했습니다."));
      return;
    }

    if (!checkoutKey || !customerKey) {
      setError("결제 인증 정보가 올바르지 않습니다.");
      return;
    }
    bankTransferAutoConfirmApiService
      .activate(companyId, checkoutKey, { authKey, customerKey })
      .then(done)
      .catch((e) => setError(e instanceof Error ? e.message : "서비스 활성화에 실패했습니다."));
  }, [companyId, navigate, params]);

  return (
    <CallbackShell>
      {error ? (
        <>
          <CircleX className="text-rose-500" size={45} />
          <h1 className="mt-5 text-xl font-bold text-[#252b38]">자동확인을 시작하지 못했습니다</h1>
          <p className="mt-2 text-sm text-[#777f90]">{error}</p>
          {/*
            뒤로 가기로 보내지 않는다. 직전 기록은 결제사 인증 주소라 되돌아가면
            이미 쓴 인증값으로 같은 실패를 반복하게 된다. 신청 화면에서 다시 시작한다.
          */}
          <button
            onClick={() =>
              navigate(companyId ? `/company/${companyId}/payment/bank-transfer` : "/", {
                replace: true,
              })
            }
            className="mt-7 rounded-xl bg-[#3157d5] px-6 py-3 text-sm font-bold text-white"
          >
            돌아가기
          </button>
        </>
      ) : (
        <>
          <div className="relative">
            <BadgeCheck className="text-emerald-500" size={48} />
            <RefreshCw className="absolute -bottom-2 -right-3 animate-spin rounded-full bg-white p-1 text-[#3157d5]" size={23} />
          </div>
          <h1 className="mt-5 text-xl font-bold text-[#252b38]">결제를 확인했습니다</h1>
          <p className="mt-2 text-sm text-[#777f90]">계좌를 등록하고 자동확인을 시작하는 중입니다.</p>
        </>
      )}
    </CallbackShell>
  );
};

export const BankTransferBillingFail: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const companyId = Number(params.get("companyId"));
  const message = params.get("message") ?? "카드 인증이 취소되었거나 실패했습니다.";
  return (
    <CallbackShell>
      <CircleX className="text-rose-500" size={48} />
      <h1 className="mt-5 text-xl font-bold text-[#252b38]">카드 인증을 완료하지 못했습니다</h1>
      <p className="mt-2 text-sm text-[#777f90]">{message}</p>
      <button
        onClick={() =>
          navigate(
            companyId ? `/company/${companyId}/payment/bank-transfer` : "/",
            { replace: true },
          )
        }
        className="mt-7 rounded-xl bg-[#3157d5] px-6 py-3 text-sm font-bold text-white"
      >
        다시 시도하기
      </button>
    </CallbackShell>
  );
};

const CallbackShell = ({ children }: { children: React.ReactNode }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#f6f7f9] p-5">
    <div className="flex w-full max-w-[500px] flex-col items-center rounded-2xl border border-[#e2e5eb] bg-white px-8 py-12 text-center shadow-sm">
      {children}
    </div>
  </div>
);
